import React, { useEffect, useState } from 'react';

const LAST_EMAIL_STORAGE_KEY = 'rivals-admin-last-email';

export default function AdminAccessPanel({ session, title = 'Admin access required' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    try {
      const savedEmail = window.localStorage.getItem(LAST_EMAIL_STORAGE_KEY);
      if (savedEmail) setEmail(savedEmail);
    } catch {
      // localStorage unavailable (e.g. private browsing) - ignore, field just stays empty.
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setLocalError('');

    try {
      await session.login(email, password, remember);
      try {
        window.localStorage.setItem(LAST_EMAIL_STORAGE_KEY, email);
      } catch {
        // localStorage unavailable - autocomplete convenience only, safe to skip.
      }
    } catch (error) {
      setLocalError(error.message || 'Unable to sign in.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-slate-950/70 p-6 shadow-[0_24px_80px_rgba(2,6,23,0.4)]">
      <p className="text-xs font-bold uppercase tracking-[0.28em] text-slate-400">CMS Access</p>
      <h2 className="mt-2 text-2xl font-semibold text-white">{title}</h2>
      <p className="mt-3 text-slate-300">Sign in with an authorized admin account to manage content, save drafts, and open preview mode.</p>
      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <input
          type="email"
          name="email"
          id="admin-login-email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="admin@example.com"
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition-colors focus:border-cyan-400/50"
          autoComplete="username"
        />
        <input
          type="password"
          name="password"
          id="admin-login-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Password"
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition-colors focus:border-cyan-400/50"
          autoComplete="current-password"
        />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-white/5 text-cyan-400 accent-cyan-400 focus:ring-cyan-400/50"
          />
          Remember this device
        </label>
        {(localError || session.error) && <p className="text-sm text-rose-300">{localError || session.error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full border border-cyan-400/40 bg-cyan-500/15 px-5 py-2 font-semibold text-cyan-200 transition-colors hover:bg-cyan-500/25 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Signing in...' : 'Sign in as admin'}
        </button>
      </form>
    </div>
  );
}